import { useMemo, useState, useCallback } from "react";
import type { Answers } from "@/schema/quizSchema";
import { deriveProfile } from "../profile";
import { OFFER } from "../offer";
import { ResultPage } from "./ResultPage";
import { ProjectionPage } from "./ProjectionPage";
import { ProductPage } from "./ProductPage";
import { ApplyingPage } from "./ApplyingPage";

type Page = "result" | "projection" | "product" | "applying";

/**
 * The post-result funnel. The engine exposes a single renderResult hook, so all
 * surfaces live here behind a local page router (the pattern AURAL and the B.O.
 * quiz both use).
 *
 * The product page is the exit: its CTA runs a brief hold, then hands off to the
 * live PDP. There is no in-quiz offer screen, because price and discount live on
 * Manly, which is the only place they stay current.
 */
export function HormoneFunnel({ answers, trackCta }: { answers: Answers; trackCta?: () => void }) {
  const profile = useMemo(() => deriveProfile(answers), [answers]);
  const [page, setPage] = useState<Page>("result");

  const toPdp = useCallback(() => {
    if (typeof window === "undefined") return;
    const utm =
      "utm_source=quiz&utm_medium=quiz&utm_campaign=endocrine-exposure-check" +
      `&utm_content=${encodeURIComponent(profile.exposure.toLowerCase())}`;
    window.location.href = `${OFFER.pdpHref}?${utm}`;
  }, [profile.exposure]);

  return (
    <div className="qk-manly">
      {page === "result" && (
        <ResultPage profile={profile} onNext={() => setPage("projection")} />
      )}
      {page === "projection" && (
        <ProjectionPage profile={profile} onNext={() => setPage("product")} />
      )}
      {page === "product" && (
        <ProductPage
          profile={profile}
          onNext={() => {
            // The CTA tap; the applying hold gives the keepalive POST time.
            trackCta?.();
            setPage("applying");
          }}
        />
      )}
      {page === "applying" && <ApplyingPage onDone={toPdp} />}
    </div>
  );
}

export default HormoneFunnel;
